import { onScopeDispose, ref, watch } from 'vue';

import { suggestionKey } from '../utils/score-suggestions';

// EN-812: how long a fixed card shows its success state before it leaves.
export const RESOLVED_DISPLAY_MS = 1000;

// What a card's anchor reads on the page, or null when there is no such element.
const anchorText = (anchor) => (anchor ? document.getElementById(anchor)?.textContent ?? null : null);

/**
 * The Resume Score cards to display, following the score's `suggestions` but letting a card that
 * leaves it go the way EN-812 asks:
 *
 * - Resolved: the user fixed it. It stays where it was for RESOLVED_DISPLAY_MS, flagged
 *   `resolved` for its success state, then leaves. Nothing takes its place: the server never
 *   gives a spent reveal to another fix.
 * - Obsolete: what it pointed at (a bullet, an entry) was deleted first. It leaves at once,
 *   silently.
 * - Moot: it left while what it pointed at is unchanged since the last score, so the user didn't
 *   fix it: fixing another bullet took its leaf to full points, say. It leaves at once, silently.
 *
 * A card is told fixed by its anchor's text having changed since the last score. One that points
 * at nothing on the page (margins, font size) always resolves, and one whose fix leaves the text
 * alone (reordering entries) leaves silently.
 *
 * When `context` changes (another resume, another target role) the cards are swapped outright:
 * the old ones weren't fixed.
 *
 * @param {import('vue').Ref<Array<object>>} suggestions The score's cards, in ranked order.
 * @param {import('vue').Ref<string>} context What the cards were scored for.
 * @returns {{ displayed: import('vue').Ref<Array<object>> }} The cards, each with `resolved`.
 */
export function useResolvingSuggestions(suggestions, context) {
	const displayed = ref([]);
	// Each shown card's anchor text as of the last score, for the cards whose anchor is on the page.
	const shownAnchorText = new Map();
	const timers = new Map();
	let shownContext = null;

	const stopTimer = (key) => {
		clearTimeout(timers.get(key));
		timers.delete(key);
	};

	const remove = (key) => {
		stopTimer(key);
		shownAnchorText.delete(key);
		displayed.value = displayed.value.filter((card) => suggestionKey(card) !== key);
	};

	const wasFixed = (card) => {
		const key = suggestionKey(card);
		const before = shownAnchorText.get(key);
		const now = anchorText(card.anchor);

		if (before === undefined) {
			return now === null;
		}

		return now !== null && now !== before;
	};

	watch([suggestions, context], ([next]) => {
		const cards = (next ?? []).map((card) => ({ ...card, resolved: false }));
		const nextKeys = new Set(cards.map(suggestionKey));

		if (context.value !== shownContext) {
			timers.forEach((_, key) => stopTimer(key));
			shownAnchorText.clear();
			shownContext = context.value;
		} else {
			displayed.value.forEach((card, index) => {
				const key = suggestionKey(card);

				if (nextKeys.has(key)) {
					stopTimer(key);

					return;
				}

				if (!card.resolved && !wasFixed(card)) {
					shownAnchorText.delete(key);

					return;
				}

				if (!card.resolved) {
					timers.set(key, setTimeout(() => remove(key), RESOLVED_DISPLAY_MS));
				}

				cards.splice(Math.min(index, cards.length), 0, { ...card, resolved: true });
			});
		}

		cards.forEach((card) => {
			const text = card.resolved ? null : anchorText(card.anchor);

			if (text !== null) {
				shownAnchorText.set(suggestionKey(card), text);
			}
		});

		displayed.value = cards;
	}, { immediate: true });

	onScopeDispose(() => {
		timers.forEach((_, key) => stopTimer(key));
	});

	return { displayed };
}
